// Accept or decline from the interviewer's own round row (D17, CR-083 "Acknowledgment Pending -> Accepted/Declined."):
// the design system's AckPair on the holder's own Pending (or unanswered) row, the status pill on every other. Accept is
// `panel.accept`; Decline opens DeclineDialog and is `panel.decline`, which notifies the Hiring Manager, the Recruiter
// and the Coordinator and opens the panel change — after it the row shows that change's status. Whose row it is, the
// status and the change are the server's (`packet.list_mine` / `panel.my_acknowledgments`); another person's row is
// drawn read-only, never with the pair.
//
// `AckCell` is the cell from props (no writes), for the SSR suite; `AckControl` adds the writes and the dialog.

import { useState } from "react";
import { AckPair, Pill, type AckStatus, type Instant } from "@/mc";
import { panelAccept, panelDecline } from "@/api/tools";
import { useWrite, errorText } from "@/api/useTool";
import { DeclineDialog } from "./DeclineDialog";
import { changeStatusWord } from "./PanelChangeReview";
import "./change.css";

/** The pair's status from the server's row: an unanswered seat past the threshold reads as nonresponse. */
export function ackStatusOf(status: string | null | undefined, nonresponse?: boolean | null): AckStatus {
  if (status === "accepted" || status === "declined") return status;
  return nonresponse ? "nonresponse" : "pending";
}

export interface AckCellProps {
  status: string | null | undefined;
  nonresponse?: boolean | null | undefined;
  /** The viewer holds this seat; anyone else sees the status only. */
  own: boolean;
  changeStatus?: string | null | undefined;
  busy?: "accept" | "decline" | undefined;
  describedBy?: string | undefined;
  onAccept?: (() => void) | undefined;
  onDecline?: (() => void) | undefined;
  /** The row's requisition: the pair's hotspots are `ack.accept.<r>` / `ack.decline.<r>` (lower case, grammar-safe). */
  requisitionId?: string | undefined;
}

/** A requisition id as a hotspot segment (`R-1040` -> `r-1040`). */
export const hsSegment = (id: string) => id.toLowerCase().replace(/[^a-z0-9-]/g, "-");

export function AckCell({ status, nonresponse, own, changeStatus, busy, describedBy, onAccept, onDecline, requisitionId }: AckCellProps) {
  const ack = ackStatusOf(status, nonresponse);
  const at = requisitionId ? `.${hsSegment(requisitionId)}` : "";
  return (
    <span className="pchg-ack">
      <AckPair
        status={ack}
        mode={own ? "edit" : "view"}
        busy={busy}
        describedBy={describedBy}
        onAccept={onAccept}
        onDecline={onDecline}
        hs={own ? { accept: `ack.accept${at}`, decline: `ack.decline${at}` } : undefined}
      />
      {ack === "declined" && changeStatus ? <Pill >{changeStatusWord(changeStatus)}</Pill> : null}
    </span>
  );
}

export interface AckControlProps {
  acknowledgmentId: string;
  requisitionId: string;
  status: string | null | undefined;
  nonresponse?: boolean | null | undefined;
  /** The acknowledgment's version when the read carries it (optional on both tools). */
  version?: number | null | undefined;
  roundLabel: string;
  interviewStart?: Instant | null | undefined;
  withinWindow?: boolean | undefined;
  noticeHours?: number | undefined;
  changeStatus?: string | null | undefined;
  /** Rows from `packet.list_mine` and `panel.my_acknowledgments` are the viewer's own; default true. */
  own?: boolean | undefined;
  describedBy?: string | undefined;
}

export function AckControl({
  acknowledgmentId, requisitionId, status, nonresponse, version, roundLabel, interviewStart, withinWindow, noticeHours,
  changeStatus, own = true, describedBy,
}: AckControlProps) {
  const invalidate = [["packets"], ["my-acks"], ["today"], ["panel-change"]] as const;
  const stamp = version != null ? { expected_version: version } : {};
  const accept = useWrite((_: void, key: string) =>
    panelAccept({ requisition_id: requisitionId, acknowledgment_id: acknowledgmentId, ...stamp }, key), invalidate);
  const decline = useWrite((input: { reason: string; notified_manager: boolean }, key: string) =>
    panelDecline({
      requisition_id: requisitionId,
      acknowledgment_id: acknowledgmentId,
      reason: input.reason,
      ...(input.notified_manager ? { notified_manager: true } : {}),
      ...stamp,
    }, key), invalidate);
  const [declining, setDeclining] = useState(false);
  return (
    <>
      <AckCell
        status={status}
        nonresponse={nonresponse}
        own={own}
        changeStatus={changeStatus}
        describedBy={describedBy}
        requisitionId={requisitionId}
        busy={accept.isPending ? "accept" : decline.isPending ? "decline" : undefined}
        onAccept={() => { void accept.mutateAsync(undefined).catch(() => undefined); }}
        onDecline={() => setDeclining(true)}
      />
      {accept.error ? <span className="pchg-note" role="alert">{errorText(accept.error)}</span> : null}
      <DeclineDialog
        open={declining}
        roundLabel={roundLabel}
        interviewStart={interviewStart}
        withinWindow={withinWindow}
        noticeHours={noticeHours}
        busy={decline.isPending}
        error={decline.error ?? undefined}
        onClose={() => setDeclining(false)}
        onConfirm={(input) => { void decline.mutateAsync(input).then(() => setDeclining(false)).catch(() => undefined); }}
      />
    </>
  );
}
